import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CalendarBlank, Clock, ArrowRight, BookOpen } from "@phosphor-icons/react";
import { useNavigation } from "../context/NavigationContext";
import { getArticles } from "../data/articles";
import SEOHead from "../components/SEOHead";

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const { navigateTo } = useNavigation();
  const articles = getArticles(i18n.language);

  // Prefetch ALL blog thumbnails on mount so they're warm in the browser cache.
  // Also inject <link rel="preload"> for the first 6 to hit the critical path.
  useEffect(() => {
    const injected = [];
    articles.forEach((article, idx) => {
      if (idx < 6) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = article.image;
        link.setAttribute("fetchpriority", idx < 2 ? "high" : "auto");
        document.head.appendChild(link);
        injected.push(link);
      } else {
        // Warm the HTTP cache without blocking critical path
        const img = new Image();
        img.decoding = "async";
        img.src = article.image;
      }
    });
    return () => {
      injected.forEach((l) => l.parentNode && l.parentNode.removeChild(l));
    };
  }, [articles]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const freshnessBadge = (dateStr) => {
    const published = new Date(dateStr);
    const now = new Date();
    const diffMs = now - published;
    // Future-dated articles (e.g. editorial calendar) also count as "new"
    const diffDays = Math.floor(Math.abs(diffMs) / 86_400_000);
    // Treat future-dated OR very recent as "New"
    if (diffMs < 0 || diffDays <= 7) {
      return { kind: "new", text: t("blog.newBadge") };
    }
    if (diffDays <= 30) {
      try {
        const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
        return { kind: "recent", text: rtf.format(-diffDays, "day") };
      } catch {
        return null;
      }
    }
    return null;
  };

  const categoryColors = {
    security: "from-red-500/20 to-orange-500/20 text-red-300 border-red-500/30",
    defi: "from-blue-500/20 to-cyan-500/20 text-cyan-300 border-cyan-500/30",
    education: "from-green-500/20 to-emerald-500/20 text-green-300 border-green-500/30",
    comparison: "from-purple-500/20 to-pink-500/20 text-purple-300 border-purple-500/30",
  };

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="blog-page">
      <SEOHead title={`${t("blog.title")} | Lumina Wallet`} description={t("blog.subtitle")} path="/blog" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lumina-violet/10 border border-lumina-violet/20 mb-6">
            <BookOpen size={16} weight="duotone" className="text-lumina-violet" />
            <span className="text-sm text-lumina-violet font-medium">
              {t("blog.badge")}
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            data-testid="blog-page-title"
          >
            {t("blog.title")}
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            {t("blog.subtitle")}
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {articles.map((article, index) => (
            <article
              key={article.slug}
              className="group cursor-pointer rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-lumina-violet/20 transition-all duration-300 overflow-hidden relative"
              onClick={() => navigateTo("blog-article", article.slug)}
              data-testid={`blog-card-${article.slug}`}
            >
              {(() => {
                const b = freshnessBadge(article.date);
                if (!b) return null;
                if (b.kind === "new") {
                  return (
                    <span
                      className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-lumina-violet text-white shadow-lg shadow-lumina-violet/40 animate-pulse"
                      data-testid={`blog-fresh-new-${article.slug}`}
                    >
                      {b.text}
                    </span>
                  );
                }
                return (
                  <span
                    className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-[10px] font-medium bg-black/60 backdrop-blur text-gray-200 border border-white/10"
                    data-testid={`blog-fresh-recent-${article.slug}`}
                  >
                    {b.text}
                  </span>
                );
              })()}
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="eager"
                  decoding="async"
                  fetchpriority={index < 4 ? "high" : "auto"}
                  width="800"
                  height="450"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border bg-gradient-to-r ${
                      categoryColors[article.category] || categoryColors.education
                    }`}
                  >
                    {t(`blog.categories.${article.category}`)}
                  </span>
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <CalendarBlank size={12} weight="regular" />
                    <span>{formatDate(article.date)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <Clock size={12} weight="regular" />
                    <span>{article.readTime} min</span>
                  </div>
                </div>
                <h2 className="text-lg font-semibold text-white mb-2 group-hover:text-lumina-violet transition-colors line-clamp-2">
                  {article.title}
                </h2>
                <p className="text-sm text-gray-400 line-clamp-2 mb-4">
                  {article.meta}
                </p>
                <span className="inline-flex items-center gap-1 text-sm text-lumina-violet font-medium group-hover:gap-2 transition-all">
                  {t("blog.readMore")}
                  <ArrowRight size={16} weight="bold" />
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
